import {Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ChangeDetectionStrategy} from '@angular/core';
import {NgClass, NgTemplateOutlet} from '@angular/common';
import {FaIconComponent} from '@fortawesome/angular-fontawesome';
import {faPen} from '@fortawesome/pro-solid-svg-icons';
import {deriveChangeStatus, MyCheckFieldChange, ProposalChangeStatus} from '../../../ruleManagement/proposal/mycheck-diff';
import {computeWordDiff, WordDiffPart} from './text-word-diff';
import {ProposalActionsComponent} from '../proposal-actions/proposal-actions.component';
import {ProposalStatusBadgeComponent} from '../proposal-status-badge/proposal-status-badge.component';

/**
 * Markiert projizierten Editor-Inhalt als geänderten Vorschlags-Wert, indem das Eingabefeld selbst
 * (input/textarea, nicht Label oder Überschrift) grün umrahmt wird – nur die Farbe des vorhandenen
 * 1-px-Rahmens, die Feldgröße bleibt gleich (`display: contents` auf dem Wrapper, damit die
 * Flex-/Grid-Größe des projizierten Inhalts unverändert bleibt). Checkboxen werden nie markiert; eine
 * Checkbox-Gruppe kennzeichnet sich mit dem Attribut `data-proposal-group` und wird als Ganzes
 * umrahmt. Rendert ohne `change` unverändert nur den projizierten Inhalt, ist also
 * unbedingt um jedes Feld legbar:
 *
 *   <app-change-marker [change]="proposalDiff?.fieldChanges?.get('configJson.kiModel')"
 *                       (acceptClicked)="onFieldAccepted('configJson.kiModel')"
 *                       (rejectClicked)="onFieldRejected('configJson.kiModel')">
 *     <app-ai-model-field ...></app-ai-model-field>
 *   </app-change-marker>
 *
 * **Kopfzeilen-Modus** (#2789, Figma „Änderungen prüfen“): Ist `label` gesetzt (oder `textDiff` mit
 * Text-Diff), steht über dem Feld eine Kopfzeile „Label · Badge · ✓/✗“. Das Badge zeigt den Status
 * („Geändert“/„Neu“/„Wird gelöscht“, aus `change.status` oder per `deriveChangeStatus` abgeleitet), das
 * eigene Label des projizierten `bic-input`/`bic-select`/`bic-textarea` (bzw. ein mit
 * `data-proposal-label` gekennzeichneter Titel) wird per CSS ausgeblendet, die ✓/✗ neben dem Feld
 * entfallen. Bei Status `removed` ist der Rahmen rot und der Wert durchgestrichen, die Buttons heißen
 * „Löschen übernehmen“/„Behalten“. `reviewKey` landet als `data-proposal-review` am Wrapper, damit der
 * Stepper der Review-Leiste im Chat dorthin scrollen kann. Ohne `label` bleibt alles wie vorher
 * (IDS-Facetten, Tabellenzeilen).
 *
 * Mit `[textDiff]="true"` (System-/Prüfprompt) zeigt das Feld zunächst den neuen Wert; der Umschalter
 * „Änderungen anzeigen“ blendet stattdessen eine satzweise Vorher/Nachher-Diff-Ansicht ein (unverändert
 * schwarz, ersetzt grau und durchgestrichen, neu grün), „Bearbeiten“ führt zurück zum Feld.
 *
 * `acceptClicked`/`rejectClicked` feuern nur die Absicht des Nutzers – das Anwenden/Zurücksetzen des
 * Feldwerts und das Ausblenden der eigenen Markierung übernimmt der Editor (er entfernt dafür einfach
 * den Diff-Eintrag zu diesem Pfad aus `proposalDiff`, wodurch `[change]` beim nächsten Change-Detection-
 * Lauf wieder `undefined` liefert).
 *
 * Layout: außer im `block`-, `fill`- und `textDiff`-Modus sind das Host-Element und der äußerste Wrapper
 * `display: contents` (siehe oben und `host` unten), sein
 * einziges Kind ist aber jetzt ein echtes Flex-Element (Feld + Buttons nebeneinander) statt reinem
 * `<ng-content>` – GENAU DIESES Element wird also, wenn `display: contents` es „entfernt", direkt zum
 * Flex-/Grid-Item der Umgebung, nicht mehr der projizierte Inhalt selbst. `rootClass` (Default `w-full`)
 * ist deshalb die Größenklasse, die früher auf dem projizierten Inhalt stand, wenn sich das Feld eine
 * Zeile mit Geschwistern teilt (z. B. `flex-1 min-w-0` bei „Prüfebene" neben „Identifikator", `w-[320px]
 * shrink-0` beim Identifikator selbst) – ohne Geschwister in der Zeile reicht der Default.
 *
 * ✓/✗ sind in allen Modi `app-proposal-actions` (zwei `bic-button` im Modus `iconOnly`, 80 px breit).
 *
 * Typneutral (KI/IDS/Hybrid gemeinsam nutzbar).
 */
@Component({
  selector: 'app-change-marker',
  standalone: true,
  imports: [NgClass, NgTemplateOutlet, FaIconComponent, ProposalActionsComponent, ProposalStatusBadgeComponent],
  templateUrl: './change-marker.component.html',
  styleUrl: './change-marker.component.css',
  // Normalfall: Host `display: contents`, damit das innere Flex-Element mit `rootClass` das Flex-/Grid-Item
  // der Umgebung ist. Ohne das war der Host (ein Custom Element, also inline → als Flex-Item auf
  // Inhaltsbreite) das eigentliche Item, und `rootClass` wirkte in Zeilen mit Geschwistern nicht – z. B.
  // füllte „Prüfebene" neben „Identifikator" die Zeile nicht mehr.
  // Ausnahmen, in denen der Host eine echte Box bleibt:
  // - `fill`: Der Host muss die Restfläche einnehmen und die Höhe als Flex-Spalte weitergeben.
  // - `block`/`textDiff`: Dort kann der Wrapper mehrere Kinder haben (projizierter Abschnitt bzw.
  //   Kopfzeile + Feld + Umschalter); als `contents` würden sie zu einzelnen Items der Umgebung und
  //   bekämen deren `gap` bzw. eigene Grid-Zellen.
  // `!actions` (Zeilenfelder) war schon vorher immer `contents`.
  changeDetection: ChangeDetectionStrategy.Eager,
  host: {
    '[class.flex]': 'fill',
    '[class.flex-col]': 'fill',
    '[class.flex-1]': 'fill',
    '[class.min-h-0]': 'fill',
    '[class.contents]': '!actions || (!fill && !block && !textDiff)',
  },
})
export class ChangeMarkerComponent implements OnChanges {
  @Input() change: MyCheckFieldChange | undefined;
  @Input() textDiff = false;
  /** Für Listen-/Tabellenabschnitte (Eingabeparameter, Tags, Ausgabeformat, …), deren Diff nur auf
   *  Feldebene (das ganze Array) existiert, nicht pro Zeile: zeigt statt der Feldmarkierung einen
   *  grünen Rahmen um den kompletten projizierten Block (Überschrift + Zeilen), damit die Markierung
   *  nicht an der Überschrift hängen bleibt, obwohl sich der Inhalt darunter geändert hat. */
  @Input() block = false;
  /** Größenklasse für das eigene, sichtbare Flex-Element (siehe Klassenkommentar) – nur bei Feldern
   *  relevant, die sich eine Flex-/Grid-Zeile mit Geschwistern teilen. */
  @Input() rootClass = 'w-full';
  /** Für Felder, die in einem flex-col-Elternelement die restliche Höhe füllen sollen (Systemprompt/
   *  Prüfprompt): Ohne das ist das Marker-Element ein Flex-Item mit Auto-Höhe und unterbricht die
   *  Höhenkette zwischen Elternelement und Textarea bzw. Diff-Ansicht. */
  @Input() fill = false;
  /** false: nur das Feld markieren, ohne eigene ✓/✗ – für Felder einer Zeile, über die die Zeile als
   *  Ganzes entscheidet (siehe `app-proposal-row`). */
  @Input() actions = true;
  /** true: Badge und ✓/✗ stehen in einer eigenen Überschrift des Aufrufers über dem Feld (Systemprompt/
   *  Prüfprompt mit „Mit chatBIC verbessern“ und Beschreibung). Der Marker zeigt dann weder Kopfzeile
   *  noch ✓/✗ neben dem Feld, nur Rahmen bzw. Diff-Ansicht. */
  @Input() externalHeader = false;
  /** Feld-Label für die Kopfzeile (Kopfzeilen-Modus, siehe Klassenkommentar); leer = bisheriges Verhalten. */
  @Input() label = '';
  /** Stabiler Schlüssel der Änderung für die Review-Leiste (`data-proposal-review`). */
  @Input() reviewKey?: string;
  @Output() acceptClicked = new EventEmitter<void>();
  @Output() rejectClicked = new EventEmitter<void>();

  protected readonly faPen = faPen;
  protected diffParts: WordDiffPart[] = [];
  protected showDiffView = false;

  get status(): ProposalChangeStatus {
    return this.change ? (this.change.status ?? deriveChangeStatus(this.change)) : 'changed';
  }

  /** Kopfzeile über dem Feld statt ✓/✗ daneben. */
  get headed(): boolean {
    return !!this.change && this.actions && !this.block && (!!this.label || (this.textDiff && this.diffParts.length > 0));
  }

  protected get acceptLabel(): string {
    return this.status === 'removed' ? 'Löschen übernehmen' : 'Änderung übernehmen';
  }

  protected get rejectLabel(): string {
    return this.status === 'removed' ? 'Behalten' : 'Änderung verwerfen';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.textDiff && typeof this.change?.before === 'string' && typeof this.change?.after === 'string') {
      this.diffParts = computeWordDiff(this.change.before, this.change.after);
      // Ein neuer Vorschlag startet wieder mit dem Feld (neuer Wert), statt in der zuletzt manuell
      // geöffneten Diff-Ansicht des vorherigen Vorschlags hängen zu bleiben.
      if (changes['change']) {
        this.showDiffView = false;
      }
    } else {
      this.diffParts = [];
    }
  }

  toggleDiffView(): void {
    this.showDiffView = !this.showDiffView;
  }
}
